/**
 * <s-product-gallery>: one-at-a-time product media track with thumbnail navigation.
 * Thumbnails ([data-gallery-thumb]) scroll the track ([data-gallery-track]) to the matching
 * slide ([data-gallery-slide]); swiping the track updates which thumbnail is aria-current.
 */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-gallery-track]');
    this.slides = [...this.querySelectorAll('[data-gallery-slide]')];
    this.thumbs = [...this.querySelectorAll('[data-gallery-thumb]')];
    if (!this.track || this.thumbs.length === 0) return;

    this.onThumbClick = (event) => {
      const index = this.thumbs.indexOf(event.currentTarget);
      this.goTo(index);
    };
    this.thumbs.forEach((thumb) => thumb.addEventListener('click', this.onThumbClick));

    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) this.setActive(this.slides.indexOf(entry.target));
        });
      },
      { root: this.track, threshold: 0.6 }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.thumbs?.forEach((thumb) => thumb.removeEventListener('click', this.onThumbClick));
  }

  goTo(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Scroll only the track; logical offset keeps RTL correct.
    const trackRect = this.track.getBoundingClientRect();
    const slideRect = slide.getBoundingClientRect();
    this.track.scrollBy({ left: slideRect.left - trackRect.left, behavior: reduce ? 'auto' : 'smooth' });
    this.setActive(index);
  }

  setActive(index) {
    if (index < 0) return;
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === index ? 'true' : 'false'));
    const thumb = this.thumbs[index];
    const list = thumb?.closest('[data-gallery-thumbs]');
    if (list && thumb) {
      const listRect = list.getBoundingClientRect();
      const thumbRect = thumb.getBoundingClientRect();
      if (thumbRect.left < listRect.left || thumbRect.right > listRect.right) {
        list.scrollBy({ left: thumbRect.left - listRect.left - 8, behavior: 'auto' });
      }
    }
  }
}

if (!customElements.get('s-product-gallery')) customElements.define('s-product-gallery', ProductGallery);
